<template>
  <div>
    <page-header>
      <template #title>
        <h1 class="page-title">FNOL+</h1>
      </template>
      <template #actions>
        <page-header-action
          :class="{ disabled: currentStep === 0 }"
          @click.prevent="toggleNotes"
        >
          Notes
        </page-header-action>
      </template>
    </page-header>
    <div class="row stepnav">
      <div class="col-lg-12">
        <nav>
          <ul class="pagination fnol-navigation">
            <li v-on:click="jumpToStep(currentStep - 1)">
              <a href="#" aria-label="Previous">
                <i class="material-icons">chevron_left</i>
              </a>
            </li>
            <li
              v-bind:key="index"
              v-for="(item, index) in steps"
              v-on:click="jumpToStep(index)"
              :class="{ active: currentStep === index }"
              v-show="stepIsVisible(index)"
            >
              <a href="#">{{ index + 1 }}</a>
            </li>
            <li v-on:click="jumpToStep(currentStep + 1)">
              <a href="#" aria-label="Next">
                <i class="material-icons">chevron_right</i>
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </div>
    <div class="row">
      <div class="col-lg-12">
        <div class="card mb-3">
          <transition name="slide-fade">
            <div class="overlay" v-if="!showQuestion"></div>
          </transition>

          <div class="card-body">
            <h3 class="text-center" v-text="step.body"></h3>
            <h4 class="text-center text-muted" v-text="step.helper"></h4>
            <div class="fields gutter-top">
              <div
                class="field fnol-plus-fields clearfix"
                v-bind:key="index"
                v-for="(field, index) in step.fields"
              >
                <div v-if="field.type.name == 'multi-selectlist'">
                  <multiselect
                    :options="field.options"
                    track-by="id"
                    :multiple="true"
                    label="label"
                    v-model="field.value"
                  ></multiselect>
                </div>

                <selectlist
                  v-if="field.type.name == 'selectlist'"
                  :name="field.name"
                  :label="field.label"
                  v-model="field.value"
                  placeholder="Please Select"
                  key-name="name"
                  :options="field.options"
                ></selectlist>

                <inputbox
                  :name="field.name"
                  v-model="field.value"
                  v-if="field.type.name == 'inputbox'"
                  :label="field.label"
                ></inputbox>

                <div
                  v-if="field.type.name == 'date-time-picker'"
                  style="margin-bottom:15px;"
                  class="form-group"
                >
                  <div class="col-md-4">
                    <label class="control-label">{{ field.label }}</label>
                  </div>
                  <div class="col-md-8">
                    <datetimepicker
                      :name="field.name"
                      v-model="field.value"
                      input-class="form-control"
                    ></datetimepicker>
                  </div>
                </div>

                <input-block
                  :name="field.name"
                  v-model="field.value"
                  v-if="field.type.name == 'textarea'"
                  :label="field.label"
                ></input-block>

                <div v-if="field.type.name == 'google-map'">
                  <google-map
                    :street-view="true"
                    :initial-center="initialCenter"
                    v-on:set-center="setCenter"
                    v-on:set-pov="setPov"
                  ></google-map>
                </div>
                <div v-if="field.type.name == 'google-route'">
                  <google-route v-on:set-route="setRoute"></google-route>
                </div>
                <div v-if="field.type.name == 'witnesses'">
                  <witnesses
                    :incident="incident"
                    :police-reports="false"
                    :witnessDetails="true"
                  ></witnesses>
                </div>
                <div v-if="field.type.name == 'police-reports'">
                  <witnesses
                    :incident="incident"
                    :witnessDetails="false"
                    :police-reports="true"
                  ></witnesses>
                </div>
                <div v-if="field.type.name == 'occupants'">
                  <occupants :incident="incident"></occupants>
                </div>
                <div v-if="field.type.name == 'splat-diagram'">
                  <splat-diagram v-on:damage-updated="setValue"></splat-diagram>
                </div>
                <div v-if="field.type.name == 'address-lookup'">
                  <address-lookup v-on:address="setValue"></address-lookup>
                </div>
                <div v-if="field.type.name == 'date-of-birth'">
                  <date-of-birth v-on:input="setDateOfBirth"></date-of-birth>
                </div>
                <vehicle-lookup
                  v-on:vrm="setRegistration"
                  v-on:vehicle="showVehicle"
                  :stacked="false"
                  label="VRN"
                  v-if="field.type.name == 'registration-lookup'"
                ></vehicle-lookup>
                <div
                  v-if="hasVehicle && field.type.name == 'registration-lookup'"
                >
                  <div class="well">
                    <strong>Make: </strong> {{ vehicle.make.label }} <br />
                    <strong>Model: </strong> {{ vehicle.model }} <br />
                    <strong>Engine Capacity:</strong>
                    {{ vehicle.engine_capacity }} <br />
                    <strong>Colour:</strong> {{ vehicle.colour }}
                  </div>
                </div>
              </div>
            </div>
            <div class="buttons-fnol-plus text-center">
              <button
                class="btn btn-primary btn-lg"
                v-bind:key="index"
                v-for="(action, index) in step.actions"
                v-text="action.body"
                v-on:click.prevent="actionClicked(action)"
              ></button>
            </div>
          </div>
          <div v-if="completed">
            <h3 class="text-center">First Notice of Loss Completed</h3>
            <div class="buttons">
              <a
                href="/ExampleFNOL.pdf"
                class="btn btn-primary btn-lg"
                target="_blank"
                >Download PDF</a
              >
            </div>
          </div>
        </div>
      </div>
    </div>
    <notes-window
      :show="notes"
      :incident="incident"
      v-on:toggle-note="toggleNotes"
      v-if="incident && currentStep !== 0"
    ></notes-window>
  </div>
</template>

<script>
import Notes from './../Shared/Notes.vue';
import VehicleLookup from '../../Generic/VehicleLookup.vue';
import AddressLookup from './../../Generic/AddressLookup.vue';

import Witnesses from './Sections/Witnesses.vue';
import Occupants from './Sections/Occupants.vue';
import GoogleMap from './Sections/Children/GoogleMap.vue';
import GoogleRoute from './Sections/Children/GoogleRoute.vue';
import SplatDiagram from './Sections/Children/SplatDiagram.vue';
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

export default {
  components: {
    PageHeader,
    PageHeaderAction,
    'vehicle-lookup': VehicleLookup,
    'notes-window': Notes,
    'google-map': GoogleMap,
    'google-route': GoogleRoute,
    witnesses: Witnesses,
    occupants: Occupants,
    'splat-diagram': SplatDiagram,
    'address-lookup': AddressLookup
  },

  data() {
    return {
      notes: false,
      vehicle: {},
      currentStep: 0,
      showQuestion: true,
      incident: null,
      steps: [],
      registration: null,
      completed: false,
      value: null,
      mapValue: null,
      initialCenter: {
        lat: 51.507,
        lng: 0.127
      },
      center: {
        lat: null,
        lng: null
      },
      pov: {
        heading: null,
        pitch: null
      },
      location: '',
      dateofbirth: null
    };
  },

  computed: {
    hasVehicle() {
      return Object.keys(this.vehicle).length > 0;
    },

    step() {
      return this.steps.length ? this.steps[this.currentStep] : {};
    }
  },

  mounted() {
    window.Ajax.get('/first-notice-of-loss-plus')
      .then(response => {
        this.steps.push(response.data);
      })
      .catch(error => {});
  },

  methods: {
    toggleNotes() {
      if (this.currentStep !== 0) {
        this.notes = !this.notes;
      }
    },

    setCenter(center) {
      this.center.lat = center.lat;
      this.center.lng = center.lng;
      this.location = center.location;
      this.mapValue = this.mapValue || {};
      this.mapValue.location = center.location;
      this.mapValue.latitude = center.lat;
      this.mapValue.longitude = center.lng;
    },

    setPov(pov) {
      this.pov = pov;
      this.mapValue = this.mapValue || {};
      this.mapValue.heading = pov.heading;
      this.mapValue.pitch = pov.pitch;
    },

    jumpToStep(index) {
      if (this.steps[index]) {
        this.currentStep = index;
      }
    },

    setRoute(mapValue) {
      this.mapValue = mapValue;
    },

    setRegistration(registration) {
      this.registration = registration;
    },

    showVehicle(vehicle) {
      this.vehicle = vehicle;
    },

    setValue(value) {
      this.value = value;
    },

    setDateOfBirth(value) {
      this.dateofbirth = value;
    },

    stepIsVisible(index) {
      if (this.steps.length < 10) {
        return true;
      }
      if (index >= this.currentStep - 5 && index <= this.currentStep + 5) {
        return true;
      }
      return false;
    },

    actionClicked(action) {
      this.showQuestion = false;

      let data = {
        action: action,
        step: this.step,
        incident: this.incident
      };

      if (data.step.fields) {
        data.step.fields.forEach(field => {
          if (field.name === 'vehicle_registration') {
            field.value = this.registration;
          }
          if (field.name === 'date_of_birth') {
            field.value = this.dateofbirth;
          }
          if (field.value === null) {
            field.value = this.value;
          }
        });
      }

      window.Ajax.post(`/first-notice-of-loss-plus`, data)
        .then(response => {
          let nextStep = this.steps[this.currentStep + 1];
          if (nextStep) {
            if (nextStep.name !== response.data.name) {
              this.steps.splice(this.currentStep + 1);
              this.steps.push(response.data);
              this.currentStep++;
            }
          } else {
            this.steps.push(response.data);
            this.currentStep++;
          }

          this.showQuestion = true;
          this.incident = this.step.incident;

          this.step.fields.forEach((field, index) => {
            if (field.type.name === 'multi-select-input') {
              this.$set(this.step.fields[index], 'value', []);
            } else {
              this.$set(this.step.fields[index], 'value', null);
            }
          });
        })
        .catch(error => {
          this.showQuestion = true;
        });
    }
  }
};
</script>
